import axios from 'axios';

// Cache implementation
const TIMED_CACHE_DURATION = 5 * 60 * 1000; 
const sessionGetCache = new Map();

function getCacheDuration(url, params) {
  if (url.includes('/analytics/') && params && (params.start || params.date)) {
    return null;
  }
  
  if (url.includes('/customers') || url.includes('/invoices')) {
    return TIMED_CACHE_DURATION;
  }
  return null; 
}

function buildCacheKey(method, url, params) {
  const methodUpper = (method || 'GET').toUpperCase();
  const sortedParams = params && typeof params === 'object'
    ? JSON.stringify(Object.keys(params).sort().reduce((acc, k) => { acc[k] = params[k]; return acc; }, {}))
    : '';
  return `${methodUpper}::${url || ''}::${sortedParams}`;
}

function getFromCache(config) {
  const cacheKey = buildCacheKey(config.method, config.url, config.params);
  const cached = sessionGetCache.get(cacheKey);
  
  if (cached) {
    const duration = getCacheDuration(config.url, config.params);
    const now = Date.now();
    
    if (duration === null || now - cached.timestamp < duration) {
      return cached.response;
    } else {
      sessionGetCache.delete(cacheKey);
    }
  }
  return null;
}

function setInCache(config, response) {
  const cacheKey = buildCacheKey(config.method, config.url, config.params);
  sessionGetCache.set(cacheKey, {
    timestamp: Date.now(),
    response: response
  });
}

//const BASE_URL = 'https://server.dexterrtech.com/api/';

// const BASE_URL = 'http://localhost:8000/api/';
const BASE_URL = 'https://api-dashboard.shopzo.app/api/';
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token to all requests if present and handle caching
api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('jwt');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Check cache for GET requests
    if (config.method?.toUpperCase() === 'GET') {
      const cachedResponse = getFromCache(config);
      if (cachedResponse) {
        // Return cached response in a way that axios will handle
        return Promise.reject({
          config,
          response: cachedResponse,
          isCache: true
        });
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (response.config.method?.toUpperCase() === 'GET') {
      setInCache(response.config, response);
    }
    return response;
  },
  (error) => {
    if (error.isCache) {
      return Promise.resolve(error.response);
    }
    return Promise.reject(error);
  }
);

const DEMO_FLAG_KEY = 'demo';
const isDemoMode = () => typeof window !== 'undefined' && localStorage.getItem(DEMO_FLAG_KEY) === 'true';
const setDemoMode = (value) => {
  if (typeof window === 'undefined') return;
  if (value) localStorage.setItem(DEMO_FLAG_KEY, 'true');
  else localStorage.removeItem(DEMO_FLAG_KEY);
};

// --- MOCK DATA --- //
const mockShops = [
  { id: 'shop123', name: 'SuperMart Andheri', location: 'Andheri, Mumbai', category: 'Grocery' },
  { id: 'shop124', name: 'Salon Pune', location: 'Pune, Maharashtra', category: 'Salon' },
];
const mockCustomers = [
  { phoneNumber: '9876543210', locationsVisited: ['Andheri', 'Bandra'], totalSpent: 1450, categories: ['Grocery', 'Electronics'] },
];
const mockInvoices = [
  { invoiceId: 'inv101', shopId: 'shop123', customerNumber: '9876543210', amount: 750, date: '2025-06-01', category: 'Grocery', location: 'Andheri, Mumbai', whatsappDelivery: true },
];

// --- AUTH FUNCTIONS --- //
export async function login(username, password) {
  // Demo credentials handling
  const isDemoAdmin = (username === 'admin@demo' || username === 'admin@demo.com') && password === 'demo123';
  const isDemoBusiness = (username === 'business@demo' || username === 'business@demo.com') && password === 'demo123';
  if (isDemoAdmin || isDemoBusiness) {
    const role = isDemoAdmin ? 'admin' : 'business';
    // Turn on demo mode for subsequent API calls
    setDemoMode(true);
    return Promise.resolve({
      data: {
        token: 'demo-token',
        user: {
          username,
          role,
          shopId: isDemoBusiness ? 'shop123' : null,
        },
      },
    });
  }

  // Real backend login
  const res = await api.post('/auth/login/', { email: username, password });
  // Ensure demo mode is off for real logins
  setDemoMode(false);
  return { data: {
    token: res.data.token,
    user: {
      username,
      role: res.data.role,
      shopId: res.data.company_id || null,
    }
  }};
}

export async function logout() {
  const token = localStorage.getItem('jwt');
  if (token) {
    try {
      await api.post('/auth/logout/', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) { /* ignore errors */ }
  }
  localStorage.clear();
  setDemoMode(false);
}

// --- API FUNCTIONS --- //

// 1. Create Shop (Onboard New Shop)
export async function createShop(data) {
  if (isDemoMode()) {
    return Promise.resolve({
      data: {
        success: true,
        message: 'Shop created successfully',
        shop: { id: 'shop999', ...data },
      },
    });
  }
  return api.post('business/onboard/', data);
}

// 2. Get All Shops
export async function getShops(page = 1, pageSize = 20) {
  if (isDemoMode()) {
    return Promise.resolve({ 
      data: {
        results: mockShops,
        count: mockShops.length,
        total_pages: 1,
        current_page: 1,
        page_size: pageSize,
        next: null,
        previous: null
      }
    });
  }
  return api.get('/shops/', {
    params: { page, page_size: pageSize }
  });
}

// 3. Get Shop Details
export async function getShop(shopId) {
  if (isDemoMode()) {
    const shop = mockShops.find(s => s.id === shopId);
    return Promise.resolve({ data: shop });
  }
  return api.get(`/shops/${shopId}/`);
}

// Add after getShop
export async function updateShop(shopId, data) {
  return api.put(`/shops/${shopId}/`, data);
}

// 4. Get All Customers with pagination
export async function getCustomers(page = 1, pageSize = 20) {
  if (isDemoMode()) {
    return Promise.resolve({ 
      data: { 
        results: mockCustomers,
        total_items: mockCustomers.length,
        items_per_page: 20,
        current_page: 1,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        page_numbers: [1]
      } 
    });
  }
  return api.get('/customers/', {
    params: { page, page_size: pageSize }
  }).then(response => {
    return {
      data: {
        results: response.data.results || [],
        total_items: response.data.total_items || 0,
        items_per_page: response.data.items_per_page || pageSize,
        current_page: response.data.current_page || page,
        total_pages: response.data.total_pages || 1,
        has_next: response.data.has_next || false,
        has_previous: response.data.has_previous || false,
        page_numbers: response.data.page_numbers || [1]
      }
    };
  });
}

// 5. Get Customer Details
export async function getCustomer(phoneNumber) {
  if (isDemoMode()) {
    const customer = mockCustomers.find(c => c.phoneNumber === phoneNumber);
    return Promise.resolve({ data: customer });
  }
  return api.get(`/customers/${phoneNumber}/`);
}

// 6. Get All Invoices with pagination
export async function getInvoices(page = 1, pageSize = 20) {
  if (isDemoMode()) {
    return Promise.resolve({ 
      data: { 
        results: mockInvoices,
        count: mockInvoices.length,
        total_pages: 1,
        current_page: page,
        page_size: pageSize,
        next: null,
        previous: null
      } 
    });
  }
  return api.get('/invoices/', {
    params: { page, page_size: pageSize }
  }).then(response => {
    return {
      data: {
        results: response.data.results || [],
        count: response.data.count || 0,
        total_pages: response.data.total_pages || 1,
        current_page: response.data.current_page || page,
        page_size: response.data.page_size || pageSize,
        next: response.data.next,
        previous: response.data.previous
      }
    };
  });
}

// 7. Get Invoice Details
export async function getInvoice(invoiceId) {
  if (isDemoMode()) {
    const invoice = mockInvoices.find(i => i.invoiceId === invoiceId);
    return Promise.resolve({ data: invoice });
  }
  return api.get(`/invoices/${invoiceId}/`);
}

// 7. Create Advertisement for a Shop
export async function createAdvertisement(shopId, formData) {
  // formData should be a FormData object with image, start_time, end_time
  return api.post(`/shops/${shopId}/advertisements/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// 8. Analytics Endpoints (customize as needed)
export async function getShopAnalytics() {
  if (isDemoMode()) {
    return Promise.resolve({ data: { kpis: {}, filters: {} } });
  }
  return api.get('/analytics/shops/');
}
export async function getCustomerAnalytics() {
  if (isDemoMode()) {
    return Promise.resolve({ data: { kpis: {}, filters: {} } });
  }
  return api.get('/analytics/customers/');
}
export async function getInvoiceAnalytics() {
  if (isDemoMode()) {
    return Promise.resolve({ data: { kpis: {}, filters: {} } });
  }
  return api.get('/analytics/invoices/');
}
export async function getDashboardAnalytics(params = {}) {
  if (isDemoMode()) {
    // Provide a mock response structure similar to backend
    return Promise.resolve({
      data: {
        totalInvoices: { allTime: 0, last7Days: 0, today: 0, custom: null },
        totalRevenue: { allTime: 0, last7Days: 0, today: 0, custom: null },
        uniqueCustomers: { allTime: 0, last7Days: 0, today: 0, custom: null },
        activeShops: { allTime: 0, last7Days: 0, today: 0, custom: null },
        avgInvoiceValue: { allTime: 0, last7Days: 0, today: 0, custom: null },
        invoicesPerCustomer: { allTime: 0, last7Days: 0, today: 0, custom: null },
        invoicesPerShop: { allTime: 0, last7Days: 0, today: 0, custom: null },
        revenueByShop: [],
        categoryWisePurchases: []
      }
    });
  }
  return api.get('/analytics/dashboard/', { params });
}

// 9. Send Reminder for Invoice
export async function sendInvoiceReminder(invoiceId, body = '') {
  // body can be a string (message) or an object (e.g., { amount })
  if (typeof body === 'object' && body !== null) {
    return api.post(`/invoices/${invoiceId}/send-reminder/`, body);
  }
  // If body is a string (message)
  if (typeof body === 'string' && body.trim() !== '') {
    return api.post(`/invoices/${invoiceId}/send-reminder/`, { message: body });
  }
  return api.post(`/invoices/${invoiceId}/send-reminder/`, {});
}

// 10. Send Customer Campaign
export async function sendCustomerCampaign({ customers, message, image }) {
  return api.post('/customer-campaigns/', { customers, message, image });
}

// 11. Get Pending Invoices
export async function getPendingInvoices(params = {}) {
  if (isDemoMode()) {
    return Promise.resolve({ 
      data: { 
        results: mockInvoices.filter(inv => !inv.paid),
        count: mockInvoices.length,
        total_pages: 1,
        current_page: 1,
        page_size: 20,
        next: null,
        previous: null
      } 
    });
  }
  return api.get('/invoices/pending/', { params });
}

// 12. Get Filtered Customers
export async function getFilteredCustomers(params = {}) {
  return api.get('/customers/filter/', { params });
}

// 13. Get Ads by Region
export async function getAdsByRegion(params = {}) {
  if (isDemoMode()) {
    // Mock data for testing
    return Promise.resolve({
      data: [
        {
          businessName: 'Test Business 1',
          pincode: '400001',
          ads: [
            {
              image_url: '/test-image-1.jpg',
              start_time: '2024-01-01',
              end_time: '2024-12-31',
              status: 'Active'
            }
          ]
        },
        {
          businessName: 'Test Business 2',
          pincode: '400002',
          ads: [
            {
              image_url: '/test-image-2.jpg',
              start_time: '2024-01-01',
              end_time: '2024-12-31',
              status: 'Inactive'
            }
          ]
        }
      ]
    });
  }

  try {
    const response = await api.get('/ads/by-region/', { params });
    
    // Check if it's the new paginated format
    if (response.data && response.data.results && Array.isArray(response.data.results)) {
      // New paginated format - return the whole response object
      return response;
    } 
    // Check if it's a direct array (old format)
    else if (response.data && Array.isArray(response.data)) {
      return response;
    } 
    // Check for nested data structure
    else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      // Handle nested data structure
      return { data: response.data.data };
    } 
    // Handle case where data might be under different key
    else if (response.data) {
      console.warn('Unexpected data structure from getAdsByRegion API:', response.data);
      // Still return the response to let the component handle it
      return response;
    } 
    else {
      // Return empty result structure
      console.warn('No data returned from getAdsByRegion API');
      return { 
        data: {
          results: [],
          count: 0,
          total_pages: 0,
          current_page: 1,
          page_size: 20,
          next: null,
          previous: null
        }
      };
    }
  } catch (error) {
    console.error('Error in getAdsByRegion:', error);
    throw error;
  }
}

// --- BUSINESS-LEVEL ADMIN SETTINGS --- //
/**
 * Get all business admin settings
 * @returns {Promise<{businesses: Array<{business_id: string, business_name: string, whatsapp_send_as: string}>, message: string}>}
 */
export async function getAllBusinessAdminSettings() {
  return api.get('/admin/settings/');
}

/**
 * Get specific business admin settings
 * @param {string} businessId
 * @returns {Promise<{business_id: string, business_name: string, whatsapp_send_as: string, created_at: string, updated_at: string, message: string}>}
 */
export async function getBusinessAdminSettings(businessId) {
  return api.get(`/admin/settings/${businessId}/`);
}

/**
 * Update business admin settings
 * @param {string} businessId
 * @param {string} whatsappSendAs - 'pdf' or 'link'
 * @returns {Promise}
 */
export async function updateBusinessAdminSettings(businessId, whatsappSendAs) {
  return api.put(`/admin/settings/${businessId}/`, {
    whatsapp_send_as: whatsappSendAs
  });
}

// --- ENHANCED DELIVERY PREFERENCES --- //
/**
 * Get enhanced delivery preferences for a business (includes admin override info)
 * @param {string} shopId
 * @returns {Promise<{
 *   admin_whatsapp_send_as: string,
 *   business_delivery_platform: string,
 *   effective_delivery_platform: string,
 *   effective_whatsapp_send_as: string,
 *   should_send_via_whatsapp: boolean,
 *   should_print: boolean,
 *   message: string
 * }>}
 */
export async function getEnhancedDeliveryPreference(shopId) {
  if (isDemoMode()) {
    return Promise.resolve({
      data: {
        admin_whatsapp_send_as: 'pdf',
        business_delivery_platform: 'WHATSAPP',
        effective_delivery_platform: 'WHATSAPP',
        effective_whatsapp_send_as: 'pdf',
        should_send_via_whatsapp: true,
        should_print: false,
        message: 'Success'
      }
    });
  }
  return api.get(`/shops/${shopId}/delivery-preference/`);
}

/**
 * Update business delivery preferences
 * @param {string} shopId
 * @param {string} deliveryPlatform - 'WHATSAPP', 'PRINT', or 'BOTH'
 * @returns {Promise}
 */
export async function updateBusinessDeliveryPreference(shopId, deliveryPlatform) {
  return api.put(`/shops/${shopId}/delivery-preference/`, {
    delivery_platform: deliveryPlatform
  });
}

// --- BUSINESS DELIVERY PREFERENCES (Simplified) --- //
/**
 * Get business delivery preferences (business user only)
 * @param {string} shopId
 * @returns {Promise<{
 *   delivery_platform: string,
 *   whatsapp_send_as: string
 * }>}
 */
export async function getBusinessDeliveryPreference(shopId) {
  if (isDemoMode()) {
    return Promise.resolve({
      data: {
        delivery_platform: 'WHATSAPP',
        whatsapp_send_as: 'pdf'
      }
    });
  }
  return api.get(`/business/shops/${shopId}/delivery-preference/`);
}

/**
 * Update business delivery preferences (business user only)
 * @param {string} shopId
 * @param {string} deliveryPlatform
 * @returns {Promise}
 */
export async function updateBusinessDeliveryPreferenceSimple(shopId, deliveryPlatform) {
  return api.put(`/business/shops/${shopId}/delivery-preference/`, {
    delivery_platform: deliveryPlatform
  });
}

// --- DELIVERY PREFERENCE (WHATSAPP) --- //
/**
 * Get WhatsApp delivery mode for a shop
 * @param {string} companyNumber
 * @returns {Promise<{whatsapp_send_as: 'pdf' | 'link'}>}
 */
export async function getWhatsappDeliveryMode(companyNumber) {
  if (isDemoMode()) {
    return Promise.resolve({
      data: {
        whatsapp_send_as: 'pdf'
      }
    });
  }
  return api.get(`/shops/${companyNumber}/delivery-preference/`);
}

/**
 * Update WhatsApp delivery mode for a shop
 * @param {string} companyNumber
 * @param {'pdf' | 'link'} whatsappSendAs
 * @param {'WHATSAPP' | 'SMS' | 'EMAIL' | 'BOTH_WHATSAPP_SMS'} deliveryPlatform
 * @returns {Promise}
 */
export async function updateWhatsappDeliveryMode(companyNumber, whatsappSendAs, deliveryPlatform = 'WHATSAPP') {
  return api.put(`/shops/${companyNumber}/delivery-preference/`, {
    delivery_platform: deliveryPlatform,
    whatsapp_send_as: whatsappSendAs
  });
}

// --- Error Handling Helper --- //
export function getApiError(error) {
  if (error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }
  return error.message || 'Unknown error';
}

// --- Usage --- //
// import { getShops, createShop, ... } from '../utils/api';
// Call these functions in your components/pages.
// When backend is ready, set MOCK_MODE = false.
